import {defineMessage} from 'react-intl';
import * as React from 'react';
import {setup, run} from '../../snippets/go';
import comment from '../../snippets/comment';
import {locales} from '../sampleLocations';
import {CreateShellCommand} from '../../../components/left-col';

const createIntent = (country, reader) => {
  if (reader === 'wpe') {
    const currency = locales[country].currency.toUpperCase();
    const pmTypes =
      country === 'CA'
        ? `"card_present",
      "interac_present",`
        : `"card_present",`;
    return `
{+create-intent}
func handleCreate(w http.ResponseWriter, r *http.Request) {
  if r.Method != "POST" {
    http.Error(w, http.StatusText(http.StatusMethodNotAllowed), http.StatusMethodNotAllowed)
    return
  }

  var req struct {
    PaymentIntentAmount string \`json:"amount"\`
  }

  if err := json.NewDecoder(r.Body).Decode(&req); err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("json.NewDecoder.Decode: %v", err)
    return
  }

  amount, _ := strconv.ParseInt(req.PaymentIntentAmount, 10, 64)

  // For Terminal payments, the 'payment_method_types' parameter must include
  // 'card_present' and the 'capture_method' must be set to 'manual'
  params := &stripe.PaymentIntentParams{
    Amount: stripe.Int64(amount),
    Currency: stripe.String(string(stripe.Currency${currency})),
    PaymentMethodTypes: stripe.StringSlice([]string{
      ${pmTypes}
    }),
    CaptureMethod: stripe.String("manual"),
  }
  {span response create-intent}pi{/span}, err := paymentintent.New(params)

  if err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("pi.New: %v", err)
    return
  }

  writeJSON(w, struct {
    ClientSecret string \`json:"client_secret"\`
  }{
    ClientSecret: pi.ClientSecret,
  })
}
{/create-intent}
`;
  }
};

const createLocation = (country) => {
  const location = locales[country].location;
  const city = location.address.city
    ? `
      City: stripe.String("${location.address.city}"),`
    : '';
  const state = location.address.state
    ? `
      State: stripe.String("${location.address.state}"),`
    : '';
  return `
func createLocation() *stripe.TerminalLocation {
  {+create-location}
  params := &stripe.TerminalLocationParams{
    Address: &stripe.AccountAddressParams{
      Line1: stripe.String("${location.address.line1}"),${city}${state}
      Country: stripe.String("${location.address.country}"),
      PostalCode: stripe.String("${location.address.postalCode}"),
    },
    DisplayName: stripe.String("${location.displayName}"),
  }

  l, _ := location.New(params)
  {/create-location}

  return l
}
`;
};

export const steps = {
  setup,
  'create-location': {},
  'connection-token-endpoint': {},
  'create-intent': {},
  'capture-intent-endpoint': {},
  'run-server': {
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.server.go.run-server.body',
        defaultMessage: 'Run your Go server and go to <a>localhost:4242</a>.',
      }),
      values: {
        a: (str) => (
          <a href="http://localhost:4242" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: CreateShellCommand(run),
  },
};

export default ({keys, merchant, reader, country}) => `package main

import (
  "bytes"
  "encoding/json"
  "io"
  "log"
  "net/http"
  "strconv"

  "github.com/stripe/stripe-go"
  "github.com/stripe/stripe-go/paymentintent"
  "github.com/stripe/stripe-go/terminal/connectiontoken"
  "github.com/stripe/stripe-go/terminal/location"
)

func main() {
  {+setup}
  ${comment && comment(merchant)}
  stripe.Key = "${keys.secret}"
  {/setup}

  fs := http.FileServer(http.Dir("public"))
  http.Handle("/", fs)
  http.HandleFunc("/connection_token", handleConnectionToken)
  http.HandleFunc("/create_payment_intent", handleCreate)
  http.HandleFunc("/capture_payment_intent", handleCapture)

  addr := "localhost:4242"
  log.Printf("Listening on %s ...", addr)
  log.Fatal(http.ListenAndServe(addr, nil))
}
${createLocation(country)}
// The ConnectionToken's secret lets you connect to any Stripe Terminal reader
// and take payments with your Stripe account.
// Be sure to authenticate the endpoint for creating connection tokens.
{+connection-token-endpoint}
func handleConnectionToken(w http.ResponseWriter, r *http.Request) {
  if r.Method != "POST" {
    http.Error(w, http.StatusText(http.StatusMethodNotAllowed), http.StatusMethodNotAllowed)
    return
  }

  params := &stripe.TerminalConnectionTokenParams{}
  {span response connection-token-endpoint}ct{/span}, err := connectiontoken.New(params)

  if err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("pi.New: %v", err)
    return
  }

  writeJSON(w, struct {
    Secret string \`json:"secret"\`
  }{
    Secret: ct.Secret,
  })
}
{/connection-token-endpoint}
${createIntent(country, reader)}
{+capture-intent-endpoint}
func handleCapture(w http.ResponseWriter, r *http.Request) {
  if r.Method != "POST" {
    http.Error(w, http.StatusText(http.StatusMethodNotAllowed), http.StatusMethodNotAllowed)
    return
  }

  var req struct {
    PaymentIntentID string \`json:"id"\`
  }

  if err := json.NewDecoder(r.Body).Decode(&req); err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("json.NewDecoder.Decode: %v", err)
    return
  }

  {span response capture-intent-endpoint}pi{/span}, err := paymentintent.Capture(req.PaymentIntentID, nil)

  if err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("pi.Capture: %v", err)
    return
  }

  writeJSON(w, pi)
}
{/capture-intent-endpoint}

func writeJSON(w http.ResponseWriter, v interface{}) {
  var buf bytes.Buffer
  if err := json.NewEncoder(&buf).Encode(v); err != nil {
    http.Error(w, err.Error(), http.StatusInternalServerError)
    log.Printf("json.NewEncoder.Encode: %v", err)
    return
  }
  w.Header().Set("Content-Type", "application/json")
  if _, err := io.Copy(w, &buf); err != nil {
    log.Printf("io.Copy: %v", err)
    return
  }
}
`;
